/**
 * Pure helpers for the "Test template" page: input checks, the request body
 * the send API and the sender lambda expect, and how to read the delivery
 * status the API reports afterwards.
 */

// Deliberately simple: one "@", no spaces, a dot in the domain. Enough to
// catch typos like "name@domain" or "name domain.com" before a send is queued.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` looks like a deliverable email address. */
export const isValidEmail = (value) => EMAIL_RE.test(String(value ?? '').trim())

/**
 * Split the CC field into trimmed addresses.
 *
 * "a@x.com, b@y.com" used to send " b@y.com" (leading space) and an empty
 * entry for a trailing comma.
 *
 * @returns {{emails: string[], invalid: string[]}}
 */
export const parseCcList = (text) => {
    const emails = String(text ?? '')
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
    return { emails, invalid: emails.filter((e) => !isValidEmail(e)) }
}

// What lambda-comms-internal-trigger fills by itself, per channel, so a test
// never needs a value for these. Email, push and voice add the receiver's
// details and the time substitutes (Template.create_time_substitutes) to the
// event's `others`; SMS and Slack substitute from `others` only, so even
// ${name} must come from there.
const RECEIVER_FIELDS = ['name', 'email', 'email_ccs', 'mobile_no', 'timezone']
const TIME_FIELDS = [
    'session_date',
    'session_date_pinpoint',
    'session_time_readable',
    'session_time',
    'receiver_tz',
]
export const BUILT_IN_VARIABLES = {
    email: [...TIME_FIELDS, ...RECEIVER_FIELDS],
    push_notification: [...TIME_FIELDS, ...RECEIVER_FIELDS],
    voice: [...TIME_FIELDS, ...RECEIVER_FIELDS],
    sms: [],
    slack: [],
}

const VARIABLE_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g

/** Every distinct `${name}` in the given texts, in order of first appearance. */
export const templateVariables = (...texts) => {
    const seen = []
    texts.forEach((text) => {
        for (const match of String(text ?? '').matchAll(VARIABLE_RE))
            if (!seen.includes(match[1])) seen.push(match[1])
    })
    return seen
}

/**
 * The variables a test send must supply in `others`.
 *
 * The template's own `${...}` placeholders (subject and body) plus any it
 * declares in its `others` list, minus what the sender fills itself for the
 * channel. Without them a test email showed the literal `${first_name}`, and
 * an SMS or voice test failed outright, because those channels use
 * `string.Template.substitute`, which raises on a missing name. WhatsApp
 * variables are Meta's `{{n}}` parameters, so none are asked here.
 */
export const variablesToAsk = ({ commType, texts = [], declared = [] }) => {
    if (commType === 'whatsapp') return []
    const builtIn = BUILT_IN_VARIABLES[commType] ?? []
    const declaredNames = (Array.isArray(declared) ? declared : []).filter(
        (d) => typeof d === 'string' && d.trim()
    )
    return [...templateVariables(...texts), ...declaredNames]
        .filter((v, i, all) => all.indexOf(v) === i)
        .filter((v) => !builtIn.includes(v))
}

/**
 * The body for POST /comms/template/test.
 *
 * - `mobile_no`, not `mobile`: every channel in lambda-comms-internal-trigger
 *   reads `receiver_details["mobile_no"]`.
 * - A blank email or mobile is left out, not sent as "": both feed index keys
 *   of the CommLogs table, DynamoDB rejects an empty string there, and the
 *   send then went out with no log row, so its status could never be found.
 * - `others` is always an object (the test values for the template's
 *   variables, trimmed): the API defaults a missing `others` to null, and the
 *   email sender evaluates `"attachments" in event["others"]`, which raises on
 *   null; every test send from this page failed that way.
 * - `receiverType` is the pipeline's receiver type when a pipeline is tested
 *   (the old wizard sent it); a template on its own tests as `demo_student`.
 * - `channel` is a WhatsApp template's own sending account (`default`,
 *   `dce-12`, `people-ops`), sent only when given; without it the service
 *   sends from the default account. Other channels send none.
 */
export const buildTestPayload = ({
    templateId,
    commType,
    name,
    email,
    ccs = [],
    mobile,
    receiver = {},
    others = {},
    eventId,
    eventTime,
    receiverType = 'demo_student',
    channel = '',
}) => ({
    event_id: eventId,
    event_time: eventTime,
    event_tz: 'UTC',
    template_id: templateId,
    comm_type: commType,
    receiver_type: receiverType || 'demo_student',
    ...(channel && { channel }),
    receiver_details: {
        name: String(name ?? '').trim(),
        ...(String(email ?? '').trim() && { email: String(email).trim() }),
        email_ccs: ccs,
        ...(String(mobile ?? '').trim() && {
            mobile_no: String(mobile).trim(),
        }),
        timezone: 'UTC',
        // Slack's email/member_id/channel_id, Push's push_notification_details.
        ...receiver,
    },
    others: Object.fromEntries(
        Object.entries(others ?? {}).map(([key, value]) => [
            key,
            String(value ?? '').trim(),
        ])
    ),
})

/** A readable reason from the status API's `error`, which may be a string or an object. */
const describeFailure = (error) => {
    if (!error) return 'The test communication was not delivered.'
    if (typeof error === 'string') return error
    return (
        error.warning ||
        error.error ||
        error.title ||
        error.message ||
        JSON.stringify(error)
    )
}

/**
 * Interpret one GET /comms/template/test/status/{id} response.
 *
 * @returns {{state: 'pending'|'delivered'|'failed', message: string}}
 *   pending until the API says the outcome is terminal.
 */
export const describeTestStatus = (data) => {
    if (!data?.is_terminal) {
        // The sender has handed it to the provider (SES, Meta...): say so, the
        // delivery callback can take longer than the polling window (on dev
        // it never arrives).
        if (data?.execution_status === 'success') {
            return {
                state: 'pending',
                accepted: true,
                message:
                    'Accepted by the provider. Waiting for delivery confirmation…',
            }
        }
        return {
            state: 'pending',
            message: data?.delivery_status
                ? `Still ${data.delivery_status}…`
                : 'Waiting for delivery confirmation…',
        }
    }
    if (
        data.execution_status === 'failed' ||
        data.delivery_status === 'failed'
    ) {
        return { state: 'failed', message: describeFailure(data.error) }
    }
    return { state: 'delivered', message: 'Delivered.' }
}

/**
 * The Slack recipient of a test send, from what the user typed: an email
 * address, a member id (U.../W...) or a channel id (C.../G...), the three
 * keys the sender accepts. Null when it is none of them. A test send used to
 * carry only a mobile number, which the Slack sender cannot use.
 */
export const slackRecipient = (raw) => {
    const value = String(raw ?? '').trim()
    if (!value) return null
    if (value.includes('@'))
        return isValidEmail(value) ? { email: value } : null
    if (/^[CG][A-Z0-9]{6,}$/.test(value)) return { channel_id: value }
    if (/^[UW][A-Z0-9]{6,}$/.test(value)) return { member_id: value }
    return null
}

/** Push platforms the sender knows (bhanzu_commons push_notification: GCM or APNS). */
export const PUSH_SERVICES = [
    { value: 'GCM', label: 'Android (FCM)' },
    { value: 'APNS', label: 'iOS (APNs)' },
]

/**
 * The Push recipient of a test send: the device token and its platform, as
 * receiver_details.push_notification_details. Null without a token or with an
 * unknown platform. A test send used to carry only a mobile number.
 */
export const pushRecipient = (token, service) => {
    const value = String(token ?? '').trim()
    if (!value || !PUSH_SERVICES.some((s) => s.value === service)) return null
    return { push_notification_details: { token: value, service } }
}

/**
 * Whether the recipient typed for a test send is complete, and what to send.
 *
 * - email: a name, a valid address and valid CCs;
 * - Slack: an email, member id or channel id (`slackRecipient`);
 * - Push: a device token and platform (`pushRecipient`);
 * - WhatsApp, SMS and voice: a mobile number.
 * Slack and Push never send a mobile number: their senders cannot use one.
 *
 * @param {string} commType
 * @param {{name?, email?, ccText?, mobile?, slackTarget?, pushToken?, pushService?}} fields
 * @returns {{ready: boolean, ccs: string[], mobile: string,
 *   receiver: object, emailInvalid: boolean, invalidCcs: string[],
 *   slackInvalid: boolean}}
 */
export const testRecipient = (commType, fields = {}) => {
    const {
        name = '',
        email = '',
        ccText = '',
        mobile = '',
        slackTarget = '',
        pushToken = '',
        pushService = 'GCM',
    } = fields
    const cc = parseCcList(ccText)
    const isEmail = commType === 'email'
    const usesTarget = commType === 'slack' || commType === 'push_notification'
    const receiver =
        commType === 'slack'
            ? slackRecipient(slackTarget)
            : commType === 'push_notification'
            ? pushRecipient(pushToken, pushService)
            : null
    const invalidCcs = isEmail ? cc.invalid : []
    const ready = isEmail
        ? !!String(name).trim() && isValidEmail(email) && !invalidCcs.length
        : usesTarget
        ? !!receiver
        : !!String(mobile).trim()
    return {
        ready,
        ccs: cc.emails,
        mobile: usesTarget ? '' : mobile,
        receiver: receiver ?? {},
        emailInvalid: isEmail && email !== '' && !isValidEmail(email),
        invalidCcs,
        slackInvalid:
            commType === 'slack' &&
            String(slackTarget).trim() !== '' &&
            !receiver,
    }
}
